import type { TFunction } from 'i18next';
import type { OrgLevel } from '@/api/orgAdmin';
import type { DropdownMenuItem } from '@/components/ui';
import type { OrganizationTreeNode } from './types';

/**
 * EAA-1804: pure builders for the row context menus, extracted out of
 * `HierarchyTreePanel` so the render function no longer DECLARES `orgActions`/
 * `levelActions` as closures over the whole component scope every render (see
 * `react-performance.mdc`). Each call still allocates its own item array —
 * that part is unavoidable for a menu whose entries depend on per-row state —
 * but the callers now build these once per relevant dependency change via
 * `useMemo` in `HierarchyTreePanel`, not once per render for every row
 * regardless of whether canManage/canManageTree/t actually changed.
 */

export interface BuildOrgActionsArgs {
    node: OrganizationTreeNode;
    t: TFunction;
    canManage: boolean;
    canManageTree: boolean;
    onAddChild: (node: OrganizationTreeNode) => void;
    onRename: (node: OrganizationTreeNode) => void;
    onMove: (node: OrganizationTreeNode) => void;
    onAdopt: (node: OrganizationTreeNode) => void;
    onCopyCode: (code: string) => void;
    onRetype: (node: OrganizationTreeNode) => void;
    onDeactivate: (id: string) => void;
    onReactivate: (id: string) => void;
}

/**
 * Frame 30's context menu. "Đổi loại đơn vị" replaced "Đặt cấp bậc (tier)" in
 * EAA-1838 — same slot, and it can no longer CLEAR the value, because a đơn vị
 * always has a type (see `OrgUnitType`).
 *
 * Gated on `canManage`, NOT `canManageTree`: `OrganizationDetailView.patch`
 * applies its super-admin gate to `is_active` and `parent` only, so retyping
 * is an org-admin-permitted act exactly like rename.
 */
export function buildOrgActions({
    node,
    t,
    canManage,
    canManageTree,
    onAddChild,
    onRename,
    onMove,
    onAdopt,
    onCopyCode,
    onRetype,
    onDeactivate,
    onReactivate,
}: BuildOrgActionsArgs): DropdownMenuItem[] {
    const items: DropdownMenuItem[] = [];
    if (canManageTree) {
        items.push({
            key: 'add-child',
            label: t('console.hierarchy.treePanel.actionAddChild'),
            onSelect: () => onAddChild(node),
        });
    }
    if (canManage) {
        items.push({
            key: 'rename',
            label: t('console.hierarchy.treePanel.actionRename'),
            onSelect: () => onRename(node),
        });
    }
    if (canManageTree && node.depth > 0) {
        items.push({
            key: 'move',
            label: t('console.hierarchy.treePanel.actionMoveTo'),
            onSelect: () => onMove(node),
        });
    }
    /**
     * EAA-1891: cross-tree adoption. Offered at EVERY depth, including the
     * root row (`depth === 0`), unlike "Chuyển đến…" above — a root org is
     * precisely the case this action exists for, and it is the one node the
     * in-tree move cannot express (there is no parent inside this tree to
     * re-target it to).
     */
    if (canManageTree) {
        items.push({
            key: 'adopt',
            label: t('admin.organizations.move.action'),
            onSelect: () => onAdopt(node),
        });
    }
    items.push({
        key: 'copy-code',
        label: t('console.hierarchy.treePanel.actionCopyCode'),
        onSelect: () => onCopyCode(node.organization.code),
    });
    if (canManage) {
        items.push({
            key: 'retype',
            label: t('console.hierarchy.treePanel.actionRetype'),
            onSelect: () => onRetype(node),
        });
    }
    if (canManageTree && node.depth > 0) {
        if (node.organization.is_active) {
            items.push({
                key: 'deactivate',
                label: t('console.hierarchy.treePanel.actionDeactivate'),
                danger: true,
                onSelect: () => onDeactivate(node.id),
            });
        } else {
            items.push({
                key: 'reactivate',
                label: t('console.hierarchy.treePanel.actionReactivate'),
                onSelect: () => onReactivate(node.id),
            });
        }
    }
    return items;
}

export interface BuildLevelActionsArgs {
    level: OrgLevel;
    t: TFunction;
    canManage: boolean;
    onDeactivate: (level: OrgLevel) => void;
}

export function buildLevelActions({
    level,
    t,
    canManage,
    onDeactivate,
}: BuildLevelActionsArgs): DropdownMenuItem[] {
    if (!canManage || !level.is_active) return [];
    return [
        {
            key: 'deactivate',
            label: t('console.hierarchy.treePanel.actionStopLevel'),
            danger: true,
            onSelect: () => onDeactivate(level),
        },
    ];
}
